import React from 'react';
import { Outlet } from 'react-router-dom';
import {
  Badge,
  Box,
  Card,
  Center,
  Container,
  Flex,
  Heading,
  Image,
  Text,
  VStack,
} from 'native-base';
import { OverviewPanel } from './OverviewPanel';
import { ScoreNumber } from './ScoreNumber';
import { useDashboardPageState } from '../../hooks/useDashboardPageState';
import { Loader } from '../Loader';
import { generateDateStringByTimestamp } from './utils';
import { useAffiliate } from '../../api/affiliates/useAffiliate';

const partyEmoji = '../party.svg';

const style = {
  overviewRowBorderColor: '#E5E5E5',
  overviewRowBorderStyle: 1,
  overviewRowBorderWidth: 1,
  panelBg: '#F7F5ED',
  textColor: '#F7F5ED',
  textColorDark: '#032326',

  card: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
};

export const UserDashboardPage = ({
  header,
  onPhaseDetailsClick,
  onPhaseLinkClick,
}) => {
  const { card, panelBg, textColor } = style;
  const {
    state: {
      loading: isFetchingDashboardData,
      lowest_ayble_score,
      over_time_score,
      today_score,
      user,
    },
  } = useDashboardPageState();

  const {
    affiliate,
    isInitialLoading: isAffiliateLoading,
    isLoading: isFetchingAffiliate,
  } = useAffiliate({
    affiliateId: user?.affiliate,
  });

  const {
    affiliate: referrer,
    isInitialLoading: isReferrerLoading,
    isLoading: isFetchingReferrer,
  } = useAffiliate({
    affiliateId: user?.referrer,
  });

  const loading =
    isFetchingDashboardData ||
    (user?.affiliate && (isAffiliateLoading || isFetchingAffiliate)) ||
    (user?.referrer && (isReferrerLoading || isFetchingReferrer));

  const currentStreakDays = user?.days_of_current_streak;
  const streakDays =
    typeof currentStreakDays === 'number' ? currentStreakDays : '- -';
  const compareTo = over_time_score >= today_score ? 'less' : 'more';
  const cancelDateString = generateDateStringByTimestamp(user?.canceled);

  return (
    <div
      className="dashboard-page"
      style={{
        height: '100vh',
        overflow: 'auto',
      }}
    >
      {header}

      {loading && (
        <Box backgroundColor="secondary.500" py={6} height={'100%'}>
          <Loader loading={loading} />
        </Box>
      )}

      {user && (
        <Box pb={0} bg="secondary.500">
          <Center>
            <Container maxW={'400px'}>
              <Flex direction="column" minW={'300px'}>
                <VStack space={1} className="header">
                  <Center>
                    <Heading color={textColor} textAlign="center" size="xl">
                      {user.first_name} {user.last_name}
                    </Heading>

                    <Text textAlign="center" color={textColor} fontSize="sm">
                      {user.email}
                    </Text>

                    <Text textAlign="center" color={textColor} fontSize="sm">
                      {user.id}
                    </Text>

                    {cancelDateString && (
                      <Badge
                        mt={3}
                        display={'flex'}
                        colorScheme="danger"
                        w={'100%'}
                      >
                        <Text color={'danger.900'}>
                          ⚠️ Cancellation date:{' '}
                          <Text fontWeight={'medium'}>{cancelDateString}</Text>
                        </Text>
                      </Badge>
                    )}

                    {affiliate && (
                      <Badge
                        mt={3}
                        display={'flex'}
                        colorScheme="success"
                        w={'100%'}
                      >
                        <Text color={'success.900'}>
                          Affiliate:{' '}
                          <Text fontWeight={'medium'}>{affiliate.name}</Text>
                        </Text>
                      </Badge>
                    )}

                    {referrer && (
                      <Badge
                        mt={3}
                        display={'flex'}
                        colorScheme="info"
                        w={'100%'}
                      >
                        <Text color={'info.900'}>
                          Referrer:{' '}
                          <Text fontWeight={'medium'}>{referrer.name}</Text>
                        </Text>
                      </Badge>
                    )}

                    {streakDays >= 2 && (
                      <Card
                        my={4}
                        h={'16px'}
                        w={'auto'}
                        bg={'yellow.400'}
                        style={card}
                      >
                        <Text
                          fontWeight={'semibold'}
                          fontSize={'md'}
                          color={'secondary.500'}
                          pr={'2'}
                        >
                          {`${streakDays} day streak`}
                        </Text>
                        <Image
                          size={['24px', '24px']}
                          source={{
                            uri: partyEmoji,
                          }}
                          alt="party emoji"
                        />
                      </Card>
                    )}

                    <Flex
                      direction="row"
                      w={'100%'}
                      justifyContent="space-between"
                      py={7}
                    >
                      <ScoreNumber label="Today's Score" score={today_score} />
                      <ScoreNumber
                        label="Lowest Score"
                        score={lowest_ayble_score}
                      />
                      <ScoreNumber
                        compareTo={compareTo}
                        label="Over Time"
                        score={over_time_score}
                      />
                    </Flex>
                  </Center>
                </VStack>
              </Flex>
            </Container>

            <Box
              style={{ borderTopStartRadius: 40, borderTopEndRadius: 40 }}
              w={{ base: '100%', lg: '70%' }}
              bgColor={panelBg}
            >
              <OverviewPanel
                onPhaseDetailsClick={onPhaseDetailsClick}
                onPhaseLinkClick={onPhaseLinkClick}
              />
            </Box>
          </Center>
        </Box>
      )}
      <Outlet />
    </div>
  );
};
